import React, { useState, useEffect, useMemo } from 'react';
import { Transition, Disclosure } from '@headlessui/react';
import SlideOver from '../../slide-over/slide-over';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import { Upload, Checkbox, Switch, Select } from 'antd';
import { TWButton, ToggleSwitch } from '../../ui';
import { useSelector } from 'react-redux';
import categoryApi from '../../../services/categoryApi';
import {
  TrashIcon,
  ChevronUpIcon,
  MinusCircleIcon,
  PhotoIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import CurrencyInput from 'react-currency-input-field';
import dayjs from 'dayjs';
import addOnImage from '../../../assets/add-on.svg';
import { Tooltip } from 'react-tooltip';
import { XCircleIcon, CalendarDaysIcon } from '@heroicons/react/20/solid';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import ProductScheduleModal from './components/product-schedule-modal';
import moment from 'moment';
import ListBoxComponent from './components/ListBoxComponent';
import TagSelector from './components/TagSelector';
import PlatformTags from './components/PlatformTags';
import ImageLibraryBrowseModal from './components/ImageLibraryBrowseModal';
import imageLibraryApi from '../../../services/imageLibraryApi';
import CaloriesInput from '../calories/calories-input';
import PosMappingSection from '../pos-mapping/pos-mapping-section';

dayjs.extend(customParseFormat);

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function ProductUpsertModal({
  show,
  onFormChange,
  onFormSubmit,
  onClickDeleteProduct,
  isSingleItem,
  setIsSingleItem,
  onCloseUpsert,
  productForm,
  setProductForm,
  errors,
  loading,
  selectedCategory,
}) {
  const publishingOptions = [
    {
      name: 'Published',
      description: 'This product is visible to customers.',
    },
    {
      name: 'Draft',
      description:
        'This product remains hidden from customers until published. You can unpublish it anytime.',
    },
  ];

  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const [categoryList, setCategoryList] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [dialogs, setDialogs] = useState({
    schedule: false,
  });
  const [librarySuggestions, setLibrarySuggestions] = useState([]);
  const [browseOpen, setBrowseOpen] = useState(false);

  const getCategoryList = async () => {
    try {
      setInitialLoading(true);

      const query = `companyid=${globalCompany.id}&pageSize=100&isdeleted=false`;
      const list = await categoryApi.list(query);
      setCategoryList(list.data.data || []);
    } catch (error) {
    } finally {
      setInitialLoading(false);
    }
  };

  // Her açılışta (ve şirket değişiminde) tazele — tek seferlik fetch bayat
  // ya da boş kalırsa Category select'i isim yerine ham id gösteriyordu.
  useEffect(() => {
    if (!show) return;
    getCategoryList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, globalCompany.id]);

  // Güvence: seçili kategori listede yoksa (fetch hatası, 100 kayıt
  // sayfalama sınırı, bayat liste) GUID görünmesin diye başa eklenir.
  const categoryOptions = useMemo(() => {
    const opts = (categoryList || []).map((x) => ({
      label: x.name,
      value: x.id,
    }));
    if (
      selectedCategory?.id &&
      selectedCategory?.name &&
      !opts.some((o) => o.value === selectedCategory.id)
    ) {
      opts.unshift({ label: selectedCategory.name, value: selectedCategory.id });
    }
    return opts;
  }, [categoryList, selectedCategory]);

  // Ürün adına göre debounce'lu library önerileri — yalnız bir library
  // resmi henüz seçilmemişken çalışır.
  useEffect(() => {
    if (productForm.libraryimageid) {
      setLibrarySuggestions([]);
      return undefined;
    }
    const name = (productForm.name || '').trim();
    if (name.length < 2) {
      setLibrarySuggestions([]);
      return undefined;
    }
    let active = true;
    const timer = setTimeout(() => {
      imageLibraryApi
        .suggest(`?name=${encodeURIComponent(name)}&take=6`)
        .then((res) => {
          if (active) setLibrarySuggestions(res.data?.data || []);
        })
        .catch(() => {
          if (active) setLibrarySuggestions([]);
        });
    }, 400);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [productForm.name, productForm.libraryimageid]);

  // Bir library resmi seçilince ürün ona bağlanır; yüklenen dosya temizlenir
  // (backend libraryimageid doluyken `image` alanını yok sayar).
  const selectLibraryImage = (img) => {
    setProductForm((prev) => ({
      ...prev,
      libraryimageid: img.id,
      imagepath: img.imagepath,
      image: '',
    }));
  };

  const clearLibraryImage = () => {
    setProductForm((prev) => ({
      ...prev,
      libraryimageid: '',
      imagepath: '',
      image: '',
    }));
  };

  const onClickAddItem = () => {
    setProductForm((prev) => ({
      ...prev,
      items: [...prev.items, { name: '', price: '', calories: '', showcalories: false }],
    }));
  };

  const onItemsChange = (val, key, index) => {
    const newItems = [...productForm.items];
    newItems[index][key] = val;
    setProductForm((prev) => ({
      ...prev,
      items: newItems,
    }));
  };

  const onClickDeleteItemInMultiple = (index) => {
    const newItems = [...productForm.items];
    newItems.splice(index, 1);
    setProductForm((prev) => ({
      ...prev,
      items: newItems,
    }));
  };

  const onFormSingleItemChange = (val, key) => {
    setProductForm((prev) => ({
      ...prev,
      items: [{ ...prev.items[0], [key]: val }],
    }));
  };

  const uploadButton = (
    <div>
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div
        className="dark:text-slate-200"
        style={{
          marginTop: 8,
        }}
      >
        Upload
      </div>
    </div>
  );

  return (
    <div>
      <SlideOver
        show={show}
        onClose={() => onCloseUpsert()}
        title={productForm.id ? 'Edit Product' : 'Create Product'}
        subtitle="Configure product details, pricing and visibility settings."
        maxWidth="max-w-3xl"
        footer={
          <div className="mt-5 sm:mt-6 grid z-10 sm:grid-flow-row-dense grid-cols-12 gap-3 py-4 sm:py-0">
            {productForm.id && (
              <TWButton
                variant="danger"
                icon={TrashIcon}
                disabled={loading}
                loading={loading}
                onClick={() => onClickDeleteProduct(productForm)}
                className="col-span-2 w-full"
              />
            )}
            <TWButton
              variant="default"
              label="Cancel"
              disabled={loading}
              onClick={() => onCloseUpsert()}
              className={classNames(
                productForm.id ? 'col-span-5' : 'col-span-6',
                'w-full',
              )}
            />
            <TWButton
              variant="primary"
              label={productForm.id ? 'Edit Product' : 'Add Product'}
              disabled={loading}
              loading={loading}
              onClick={() => onFormSubmit()}
              className={classNames(
                productForm.id ? 'col-span-5' : 'col-span-6',
                'w-full',
              )}
            />
          </div>
        }
      >
        <div className="relative space-y-1 dark:text-slate-200 text-left">
          <div>
            <div className=" contents sm:flex justify-between">
              <div>
                <label
                  htmlFor="image"
                  className="block pb-3 text-sm font-medium text-gray-700 dark:text-slate-400"
                >
                  Image
                </label>
                <div className="mt-1">
                  <Upload
                    multiple={false}
                    maxCount={1}
                    name="avatar"
                    onChange={(e) =>
                      setProductForm((prev) => ({
                        ...prev,
                        image: e.file,
                        libraryimageid: '',
                      }))
                    }
                    listType="picture-circle"
                    className="avatar-uploader"
                    beforeUpload={() => false}
                  >
                    {productForm.imagepath ? (
                      <div className="flex relative h-full w-full">
                        <img
                          src={productForm.imagepath}
                          alt="avatar"
                          className="min-h-[4rem] min-w-[4rem] h-16 w-16 rounded-full object-cover"
                          style={{
                            width: '100%',
                            height: '100%',
                          }}
                        />
                      </div>
                    ) : (
                      uploadButton
                    )}
                  </Upload>
                  {productForm.imagepath && (
                    <div className="flex relative h-full w-full">
                      <XCircleIcon
                        onClick={() =>
                          productForm.libraryimageid
                            ? clearLibraryImage()
                            : setProductForm((prev) => ({
                                ...prev,
                                imagepath: '',
                                image: null,
                              }))
                        }
                        className="text-gray-500 w-4 h-4 hover:text-gray-900 cursor-pointer "
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setBrowseOpen(true)}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:-translate-y-px hover:border-blue-300 hover:text-blue-700 hover:shadow dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-sky-500/50 dark:hover:text-sky-300"
                  >
                    <PhotoIcon className="h-4 w-4 text-blue-500 dark:text-sky-400" />
                    Browse image library
                  </button>
                  {librarySuggestions.length > 0 && (
                    <div className="mt-3">
                      <p className="flex items-center gap-1 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
                        <SparklesIcon className="h-3 w-3 text-amber-400" />
                        Suggested from library
                      </p>
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        {librarySuggestions.map((s) => (
                          <button
                            type="button"
                            key={s.id}
                            onClick={() => selectLibraryImage(s)}
                            title={s.name}
                            className="group/sg overflow-hidden rounded-lg border border-slate-200 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-400 hover:shadow-md dark:border-slate-600 dark:hover:border-sky-500/60"
                          >
                            <img
                              src={s.imagepath}
                              alt={s.name}
                              className="h-12 w-12 object-cover transition-transform duration-200 group-hover/sg:scale-110"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="h-2 mt-1  text-xs">
                    {errors.imagepath && (
                      <p className="text-red-700">Image can not be empty</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="pr-4 my-4 sm:my-0">
                <div>
                  <div>
                    <div className="mt-2 flex space-x-4 justify-between">
                      {productForm.isdisabled && (
                        <>
                          <span className="hidden sm:block">
                            <button
                              onClick={() =>
                                setDialogs((prev) => ({
                                  ...prev,
                                  schedule: true,
                                }))
                              }
                              type="button"
                              className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                              <CalendarDaysIcon
                                aria-hidden="true"
                                className="-ml-0.5 w-5 h-5 text-gray-400"
                              />
                              Schedule
                            </button>
                          </span>
                        </>
                      )}
                      <ListBoxComponent
                        onChange={(val) => {
                          onFormChange(val.name !== 'Published', 'isdisabled');
                        }}
                        options={publishingOptions}
                        selected={
                          productForm.isdisabled
                            ? publishingOptions[1]
                            : publishingOptions[0]
                        }
                      />
                    </div>
                  </div>
                  <div className="pt-2">
                    <div>
                      {productForm.isdisabled &&
                        productForm?.productschedules?.map((schedule) => (
                          <div className="flex items-center">
                            <p className="text-xs font-normal text-gray-500 dark:text-slate-400 whitespace-nowrap">
                              Scheduled:{' '}
                              {moment(schedule.startdate)
                                .utcOffset(0)
                                .format('MM/DD hh:mm A')}{' '}
                              to{' '}
                              {moment(schedule.enddate)
                                .utcOffset(0)
                                .format('MM/DD hh:mm A')}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Product Name*
            </label>
            <div className="mt-1">
              <input
                value={productForm.name}
                onChange={(e) => {
                  onFormChange(e.target.value, 'name');
                }}
                type="text"
                name="name"
                id="name"
                maxLength={60}
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-md dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-slate-500 dark:border-slate-500"
                placeholder="e.g ''Ceasar Salad, Chef's Burger''"
              />
              <div className="h-2 mt-1  text-xs">
                {errors.name && (
                  <p className="text-red-700">Product Name can not be empty</p>
                )}
              </div>
            </div>
          </div>
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Description
            </label>
            <div className="mt-1">
              <textarea
                onChange={(e) => {
                  onFormChange(e.target.value, 'description');
                }}
                value={productForm.description}
                rows={3}
                maxLength={400}
                type="text"
                name="description"
                id="description"
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-md dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-slate-500 dark:border-slate-500"
                placeholder="e.g ''Served with chef's special sauce and fries''"
              />
            </div>
          </div>
          <div className="py-2">
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-1"
            >
              Category
            </label>
            <Select
              loading={initialLoading}
              size="large"
              style={{
                width: '100%',
              }}
              placeholder="Please select Option Groups"
              value={productForm.categoryid || undefined}
              onChange={(val) => onFormChange(val, 'categoryid')}
              options={categoryOptions}
            />
          </div>
          {!productForm.id && (
            <div className="py-5">
              <h1 className="font-semibold mb-5 dark:text-slate-300 border-b">
                Items
              </h1>
              <div className="flex space-x-5 pb-2">
                <h1
                  onClick={() => setIsSingleItem(true)}
                  className={classNames(
                    isSingleItem ? 'border-blue-500' : '',
                    'cursor-pointer border-b-2 inline-block',
                  )}
                >
                  Single Item
                </h1>

                <h1
                  onClick={() => setIsSingleItem(false)}
                  className={classNames(
                    isSingleItem ? '' : 'border-blue-500',
                    'cursor-pointer border-b-2 inline-block',
                  )}
                >
                  Multiple Item
                </h1>
              </div>
              {isSingleItem ? (
                <div className="space-y-2 grid grid-cols-2">
                  <div>
                    <label
                      htmlFor="name"
                      className="mt-2 block text-sm font-medium text-gray-700 dark:text-slate-400"
                    >
                      Item Name
                    </label>
                    <div className="mt-1">
                      <p>{productForm.name}</p>
                    </div>
                  </div>
                  <div className="">
                    <label
                      htmlFor="price"
                      className="block text-sm font-medium leading-6 text-gray-900 dark:text-slate-400"
                    >
                      Price
                    </label>
                    <div className="relative mt-2 rounded-md shadow-sm">
                      <CurrencyInput
                        id="input-price"
                        name="price"
                        placeholder="0.00"
                        className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                        prefix="$"
                        value={productForm?.items[0]?.price}
                        decimalSeparator="."
                        groupSeparator=","
                        decimalScale={2}
                        step={1}
                        defaultValue={0}
                        decimalsLimit={2}
                        onValueChange={(value, name, values) =>
                          onFormSingleItemChange(value, name)
                        }
                      />
                    </div>
                    {errors.items[0].price && (
                      <div className="h-2 mt-1  text-xs">
                        <p className="text-red-700">
                          Price name can not be empty
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 space-y-3">
                    <div className="flex items-center gap-3">
                      <ToggleSwitch
                        checked={productForm?.items[0]?.showcalories !== false}
                        ariaLabel="Show calories"
                        onChange={(val) =>
                          onFormSingleItemChange(val, 'showcalories')
                        }
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-slate-400">
                        Show calories
                      </span>
                    </div>
                    {productForm?.items[0]?.showcalories !== false && (
                      <div>
                        <label
                          htmlFor="product-item-calories"
                          className="block text-sm font-medium leading-6 text-gray-900 dark:text-slate-400"
                        >
                          Calories{' '}
                          <span className="font-normal text-gray-400">
                            (optional)
                          </span>
                        </label>
                        <div className="mt-2">
                          <CaloriesInput
                            id="product-item-calories"
                            value={productForm?.items[0]?.calories}
                            onChange={(val) =>
                              onFormSingleItemChange(val, 'calories')
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {productForm.items?.map((item, index) => {
                    return (
                      <div className="grid grid-cols-12 space-x-5">
                        <div className="col-span-5">
                          <label
                            htmlFor="name"
                            className="mt-2  block text-sm font-medium text-gray-700 dark:text-slate-400"
                          >
                            Item Name
                          </label>
                          <div className="mt-1">
                            <input
                              value={item.name}
                              onChange={(e) =>
                                onItemsChange(e.target.value, 'name', index)
                              }
                              type="text"
                              name="name"
                              id="name"
                              className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                              placeholder="e.g ''Chicken,beef'' or ''Small, Medium''"
                            />
                            <div className="h-2 mt-1  text-xs">
                              {errors.items[index]?.name && (
                                <p className="text-red-700">
                                  Item name can not be empty
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-span-3">
                          <label
                            htmlFor="price"
                            className="block text-sm font-medium leading-6 text-gray-900"
                          >
                            Price
                          </label>
                          <div className="relative mt-2 rounded-md shadow-sm">
                            <CurrencyInput
                              id="input-price"
                              name="price"
                              placeholder="0.00"
                              className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                              prefix="$"
                              value={item.price}
                              decimalSeparator="."
                              groupSeparator=","
                              decimalScale={2}
                              step={1}
                              defaultValue={0}
                              decimalsLimit={2}
                              onValueChange={(value, name, values) =>
                                onItemsChange(value, name, index)
                              }
                            />
                          </div>
                          {errors.items[index]?.price && (
                            <div className="h-2 mt-1  text-xs">
                              <p className="text-red-700">
                                Price can not be empty
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="col-span-3">
                          <div className="flex items-center gap-2">
                            <ToggleSwitch
                              checked={item.showcalories !== false}
                              ariaLabel="Show calories"
                              onChange={(val) =>
                                onItemsChange(val, 'showcalories', index)
                              }
                            />
                            <span className="text-xs font-medium text-gray-700 dark:text-slate-400">
                              Calories
                            </span>
                          </div>
                          {item.showcalories !== false && (
                            <div className="mt-2">
                              <CaloriesInput
                                id={`product-item-calories-${index}`}
                                value={item.calories}
                                onChange={(val) =>
                                  onItemsChange(val, 'calories', index)
                                }
                              />
                            </div>
                          )}
                        </div>
                        <div className="pt-4 col-span-1  self-center justify-self-center">
                          <MinusCircleIcon
                            onClick={() => onClickDeleteItemInMultiple(index)}
                            className="text-red-500 mr-3 w-5 h-5 hover:text-red-900"
                          />
                        </div>
                      </div>
                    );
                  })}
                  <div className="col-span-12">
                    <p className="font-normal text-xs text-gray-500 dark:text-slate-400">
                      Customize your items with options like "chicken, beef,
                      lamb" or "small, medium, and large." For items without
                      customizations, simply enter the item name.
                    </p>
                  </div>

                  <div className="mt-5 sm:mt-6 sm:grid z-10 sm:grid-flow-row-dense sm:grid-cols-4 sm:gap-3">
                    <TWButton
                      variant="primary"
                      label="+ Add Item"
                      disabled={loading.upsertLoading}
                      loading={loading.upsertLoading}
                      onClick={() => onClickAddItem()}
                      className="col-span-1 col-start-1 w-full"
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <div>
            <h1 className="font-semibold my-2 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700">
              Product Settings
            </h1>
            <div className="flex items-center sm:pt-5">
              <Tooltip id="isupsell" />
              <Checkbox
                data-tooltip-id="isupsell"
                data-tooltip-html="When customers are checking out, they will be presented
                      with add-on item suggestions. You can select up to four
                      add-on items."
                className="flex"
                disabled={productForm.items.length === 0}
                checked={productForm.isupsell}
                onChange={(e) => onFormChange(e.target.checked, 'isupsell')}
              ></Checkbox>
              <div className="flex pl-2 items-center">
                <img src={addOnImage} className="w-5 h-5" alt="addOnItem" />
                <p className="text-sm font-medium text-gray-700 dark:text-slate-300">
                  Make Upsell Item
                </p>
              </div>
            </div>
          </div>
          <div className="pt-2">
            <p className="font-normal text-xs text-gray-500 dark:text-slate-400">
              When customers are checking out, they will be presented with
              add-on item suggestions. You can select up to four add-on items.
            </p>
          </div>
          <div className="w-full pt-5">
            <div className="bg-white dark:bg-slate-800">
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex w-full items-center justify-start rounded-md bg-white dark:bg-slate-800 py-2 text-left text-sm font-medium text-black   focus-visible:ring-opacity-75">
                      <div className="grid leading-6 pr-2">
                        <span className=" font-semibold text-md dark:text-slate-300">
                          Advanced Settings
                        </span>
                      </div>
                      <ChevronUpIcon
                        className={`${
                          open ? 'rotate-180 transform' : ''
                        } h-5 w-5 text-black dark:text-slate-200`}
                      />
                    </Disclosure.Button>
                    <Transition
                      show={open}
                      enter="transition duration-100 ease-out"
                      enterFrom="transform scale-95 opacity-0"
                      enterTo="transform scale-100 opacity-100"
                      leave="transition duration-75 ease-out"
                      leaveFrom="transform scale-100 opacity-100"
                      leaveTo="transform scale-95 opacity-0"
                    >
                      <Disclosure.Panel className="pt-4 text-sm space-y-6">
                        <PlatformTags
                          value={productForm.productsettings || []}
                          onChange={(value) =>
                            onFormChange(value, 'productsettings')
                          }
                        />

                        <TagSelector
                          selectedTags={productForm.customproducttags || []}
                          onChange={(tags) =>
                            onFormChange(tags, 'customproducttags')
                          }
                          label="Custom Tags"
                          productId={productForm.id}
                        />

                        <div className="grid grid-cols-[7.75rem,1fr] gap-x-4 gap-y-6">
                          <div>
                            <label
                              htmlFor="default"
                              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
                            >
                              Use Tax
                            </label>
                            <div className="mt-2">
                              <Switch
                                checked={productForm.usetax}
                                onChange={(e) => {
                                  onFormChange(e, 'usetax');
                                }}
                                className={classNames(
                                  productForm.usetax
                                    ? 'bg-blue-600'
                                    : 'bg-gray-200',
                                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                                )}
                              >
                                <span
                                  aria-hidden="true"
                                  className={classNames(
                                    productForm.usetax
                                      ? 'translate-x-5'
                                      : 'translate-x-0',
                                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                                  )}
                                />
                              </Switch>
                            </div>
                          </div>
                          <div>
                            <label
                              htmlFor="tax"
                              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
                            >
                              Different Tax
                            </label>
                            <div className="mt-1">
                              <CurrencyInput
                                id="input-price"
                                name="tax"
                                placeholder="0.00"
                                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                                prefix="%"
                                disabled={!productForm.usetax}
                                value={productForm.tax}
                                decimalSeparator="."
                                groupSeparator=","
                                decimalScale={2}
                                step={1}
                                defaultValue={0}
                                decimalsLimit={2}
                                onValueChange={(value, name) =>
                                  onFormChange(value, 'tax')
                                }
                              />
                              <div className="h-2 mt-1  text-xs">
                                {errors.tax && (
                                  <p className="text-red-700">
                                    Please enter valid tax
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div>
                            <label
                              htmlFor="default"
                              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
                            >
                              Use CRV Tax
                            </label>
                            <div className="mt-2">
                              <Switch
                                checked={productForm.usecrvtax}
                                onChange={(e) => {
                                  onFormChange(e, 'usecrvtax');
                                }}
                                className={classNames(
                                  productForm.usecrvtax
                                    ? 'bg-blue-600'
                                    : 'bg-gray-200',
                                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                                )}
                              >
                                <span
                                  aria-hidden="true"
                                  className={classNames(
                                    productForm.usecrvtax
                                      ? 'translate-x-5'
                                      : 'translate-x-0',
                                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                                  )}
                                />
                              </Switch>
                            </div>
                          </div>
                          <div>
                            <label
                              htmlFor="tax"
                              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
                            >
                              CRV Tax
                            </label>
                            <div className="mt-1">
                              <CurrencyInput
                                id="input-price"
                                name="crvtaxrate"
                                placeholder="0.00"
                                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                                prefix="$"
                                disabled={!productForm.usecrvtax}
                                value={productForm.crvtaxrate}
                                decimalSeparator="."
                                groupSeparator=","
                                decimalScale={2}
                                step={1}
                                defaultValue={0}
                                decimalsLimit={2}
                                onValueChange={(value, name, values) =>
                                  onFormChange(value, name)
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </Disclosure.Panel>
                    </Transition>
                  </>
                )}
              </Disclosure>
            </div>
          </div>
          <PosMappingSection
            level="product"
            squareid={productForm.squareid}
            squarename={productForm.squarename}
            squaremissing={productForm.squaremissing}
            onChange={(id, name) => {
              onFormChange(id, 'squareid');
              onFormChange(name, 'squarename');
            }}
          />
          <div className="h-2 mt-1  text-xs"></div>
        </div>
      </SlideOver>
      <ProductScheduleModal
        show={dialogs.schedule}
        product={productForm}
        setProductForm={setProductForm}
        onClose={() =>
          setDialogs((prev) => ({
            ...prev,
            schedule: false,
          }))
        }
      />
      <ImageLibraryBrowseModal
        open={browseOpen}
        onClose={() => setBrowseOpen(false)}
        onSelect={selectLibraryImage}
      />
    </div>
  );
}
